/**
 * Screenshot metadata registry.
 *
 * Coordinates from `[POINT:screen=N,x=X,y=Y]` and MCP `screenshot_point`
 * are screenshot-local pixels. To render the Denker cursor, we must
 * invert those pixels through the exact monitor metadata captured with
 * that screenshot: display id, scale factor, native size, screenshot
 * size, and display origin.
 */
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";

import {
  useOutboundScreenshotStore,
  type StoredOutboundScreens as StoredScreens,
} from "./outbound-screenshot-store";
import { mirrorPromptScreens } from "./prompt-screens-mirror";
const CAPTURE_CONTEXT_TTL_MS = 10 * 60 * 1000;
const PROMPT_SCREEN_CONTEXT_TTL_MS = 30 * 1000;
const MAX_CAPTURE_CONTEXTS = 64;
const MAX_IMAGE_CONTEXTS = 128;
const MAX_BOUND_CONTEXTS = 64;
const MAX_PENDING_DISPATCHES = 16;

export interface OutboundCaptureContext {
  capture_id: string;
  execution_id: string;
  cli_session_id?: string | null;
  screen: ScreenshotScreenMeta;
}

interface StoredCaptureContext extends OutboundCaptureContext {
  registeredAt: number;
}

const captureContexts = new Map<string, StoredCaptureContext>();

function purgeCaptureContexts(now = Date.now()): void {
  for (const [captureId, context] of captureContexts) {
    if (now - context.registeredAt > CAPTURE_CONTEXT_TTL_MS) {
      captureContexts.delete(captureId);
    }
  }
}

export function registerOutboundCaptureContexts(
  contexts: OutboundCaptureContext[],
): void {
  purgeCaptureContexts();
  for (const context of contexts) {
    captureContexts.delete(context.capture_id);
    captureContexts.set(context.capture_id, {
      ...context,
      screen: { ...context.screen },
      registeredAt: Date.now(),
    });
    while (captureContexts.size > MAX_CAPTURE_CONTEXTS) {
      const oldestCaptureId = captureContexts.keys().next().value;
      if (typeof oldestCaptureId !== "string") break;
      captureContexts.delete(oldestCaptureId);
    }
  }
}

function screensForCapture({
  captureId,
  executionId,
  cliSessionId,
}: {
  captureId: string;
  executionId?: string;
  cliSessionId?: string;
}): ScreenshotScreenMeta[] {
  purgeCaptureContexts();
  const context = captureContexts.get(captureId);
  if (!context) return [];
  if (!executionId || context.execution_id !== executionId) return [];
  if (context.cli_session_id && context.cli_session_id !== cliSessionId) {
    return [];
  }
  return [{ ...context.screen }];
}

function cloneScreens(screens: ScreenshotScreenMeta[]): ScreenshotScreenMeta[] {
  return screens.map((screen) => ({ ...screen }));
}

function storeScreens(screens: ScreenshotScreenMeta[]): StoredScreens {
  return { screens: cloneScreens(screens), registeredAt: Date.now() };
}

function freshScreens(
  stored: StoredScreens | undefined | null,
  maxAgeMs = PROMPT_SCREEN_CONTEXT_TTL_MS,
) {
  if (!stored || Date.now() - stored.registeredAt > maxAgeMs) {
    return null;
  }
  return stored;
}

function setBoundContext(
  contexts: Array<[string, StoredScreens]>,
  id: string,
  screens: StoredScreens,
  limit: number,
): Array<[string, StoredScreens]> {
  return [
    ...contexts.filter(([key]) => key !== id),
    [id, screens] as [string, StoredScreens],
  ].slice(-limit);
}

function boundContext(contexts: Array<[string, StoredScreens]>, id: string) {
  return contexts.find(([key]) => key === id)?.[1];
}

export function setOutboundScreens(screens: ScreenshotScreenMeta[]): void {
  useOutboundScreenshotStore.setState({ lastScreens: storeScreens(screens) });
}

export function registerOutboundScreensForImages(
  imageIds: string[],
  screens: ScreenshotScreenMeta[],
): void {
  if (imageIds.length === 0 || screens.length === 0) return;
  const stored = storeScreens(screens);
  useOutboundScreenshotStore.setState((state) => ({
    lastScreens: stored,
    screensByImageId: imageIds.reduce(
      (contexts, imageId) =>
        setBoundContext(contexts, imageId, stored, MAX_IMAGE_CONTEXTS),
      state.screensByImageId,
    ),
  }));
}

function screensForImageIds(
  imageIds: string[] | undefined,
): StoredScreens | null {
  for (const imageId of imageIds ?? []) {
    const screens = freshScreens(
      boundContext(
        useOutboundScreenshotStore.getState().screensByImageId,
        imageId,
      ),
    );
    if (screens) return screens;
  }
  return null;
}

export function registerOutboundScreensForDispatch({
  clientRequestId,
  imageIds,
  executionId,
  sessionId,
}: {
  imageIds?: string[];
  clientRequestId?: string;
  executionId?: string;
  sessionId?: string;
}): void {
  const screens = screensForImageIds(imageIds);
  if (!screens) return;
  void mirrorPromptScreens(executionId, screens.screens);
  useOutboundScreenshotStore.setState((state) => ({
    ...(clientRequestId
      ? {
          screensByRequestId: setBoundContext(
            state.screensByRequestId,
            clientRequestId,
            screens,
            MAX_BOUND_CONTEXTS,
          ),
        }
      : {}),
    ...(executionId
      ? {
          screensByExecutionId: setBoundContext(
            state.screensByExecutionId,
            executionId,
            screens,
            MAX_BOUND_CONTEXTS,
          ),
        }
      : {}),
    ...(sessionId
      ? {
          screensBySessionId: setBoundContext(
            state.screensBySessionId,
            sessionId,
            screens,
            MAX_BOUND_CONTEXTS,
          ),
        }
      : {}),
    ...(!clientRequestId && !executionId && !sessionId
      ? {
          pendingDispatches: [...state.pendingDispatches, screens].slice(
            -MAX_PENDING_DISPATCHES,
          ),
        }
      : {}),
  }));
}

export function bindPendingOutboundScreens({
  clientRequestId,
  executionId,
  sessionId,
}: {
  clientRequestId?: string;
  executionId?: string;
  sessionId?: string;
}): void {
  if (!executionId && !sessionId) return;
  const mirrorAfterBinding = () => {
    if (!executionId) return;
    // Read back what binding actually settled on rather than guessing: a
    // pending dispatch may have supplied the screens, and only the store knows
    // which one won.
    const bound = getOutboundScreensForContext({
      executionId,
      requireBoundContext: true,
    });
    void mirrorPromptScreens(executionId, bound);
  };
  useOutboundScreenshotStore.setState((state) => {
    let screens = clientRequestId
      ? freshScreens(boundContext(state.screensByRequestId, clientRequestId))
      : ((executionId
          ? freshScreens(boundContext(state.screensByExecutionId, executionId))
          : null) ??
        (sessionId
          ? freshScreens(boundContext(state.screensBySessionId, sessionId))
          : null));
    let consumed = 0;
    while (
      !clientRequestId &&
      !screens &&
      consumed < state.pendingDispatches.length
    ) {
      screens = freshScreens(state.pendingDispatches[consumed++]);
    }
    return {
      ...(consumed
        ? { pendingDispatches: state.pendingDispatches.slice(consumed) }
        : {}),
      ...(screens && executionId
        ? {
            screensByExecutionId: setBoundContext(
              state.screensByExecutionId,
              executionId,
              screens,
              MAX_BOUND_CONTEXTS,
            ),
          }
        : {}),
      ...(screens && sessionId
        ? {
            screensBySessionId: setBoundContext(
              state.screensBySessionId,
              sessionId,
              screens,
              MAX_BOUND_CONTEXTS,
            ),
          }
        : {}),
    };
  });
  mirrorAfterBinding();
}

export function getOutboundScreensForContext({
  captureId,
  imageIds,
  executionId,
  sessionId,
  cliSessionId,
  requireBoundContext,
  boundContextMaxAgeMs,
}: {
  captureId?: string;
  imageIds?: string[];
  executionId?: string;
  sessionId?: string;
  cliSessionId?: string;
  requireBoundContext?: boolean;
  boundContextMaxAgeMs?: number;
} = {}): ScreenshotScreenMeta[] {
  if (captureId) {
    return screensForCapture({ captureId, executionId, cliSessionId });
  }
  const state = useOutboundScreenshotStore.getState();
  const boundScreens =
    (executionId
      ? freshScreens(
          boundContext(state.screensByExecutionId, executionId),
          boundContextMaxAgeMs,
        )
      : null) ??
    (sessionId
      ? freshScreens(
          boundContext(state.screensBySessionId, sessionId),
          boundContextMaxAgeMs,
        )
      : null);
  if (boundScreens) return cloneScreens(boundScreens.screens);
  if (requireBoundContext) return [];
  const fallback =
    screensForImageIds(imageIds) ??
    freshScreens(useOutboundScreenshotStore.getState().lastScreens);
  return fallback ? cloneScreens(fallback.screens) : [];
}

export function getOutboundScreens(): ScreenshotScreenMeta[] {
  const screens = freshScreens(
    useOutboundScreenshotStore.getState().lastScreens,
  );
  return screens ? cloneScreens(screens.screens) : [];
}

export function clearOutboundScreens(): void {
  useOutboundScreenshotStore.setState({
    lastScreens: null,
    screensByImageId: [],
    screensByRequestId: [],
    screensByExecutionId: [],
    screensBySessionId: [],
    pendingDispatches: [],
  });
  captureContexts.clear();
}
